import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  MobileStepper,
  Paper,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Store as StoreIcon,
  Palette as PaletteIcon,
  Toll as CreditIcon,
  Brush as CustomizeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { hasSeenEducationModal, markEducationModalSeen, resetEducationModal } from '../../lib/educationModals';

interface EducationStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  visual: React.ReactNode;
}

const ThemeGridVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
    {['#1976d2', '#2e7d32', '#9c27b0'].map((color, i) => (
      <Paper
        key={i}
        elevation={2}
        sx={{
          width: 70,
          height: 70,
          p: 0.5,
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            bgcolor: color,
            borderRadius: 0.5,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1px',
            p: 0.5,
          }}
        >
          {[...Array(9)].map((_, j) => (
            <Box
              key={j}
              sx={{
                bgcolor: j % 2 === 0 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                borderRadius: '1px',
              }}
            />
          ))}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: 8,
            textAlign: 'center',
            color: 'text.secondary',
            mt: 0.25,
          }}
        >
          {['Classic', 'Emerald', 'Purple'][i]}
        </Typography>
      </Paper>
    ))}
  </Box>
);

const CreditSystemVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1.5 }}>
      {[1, 1, 1, 1, 1].map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: i < 3 ? 'warning.main' : 'grey.300',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CreditIcon sx={{ fontSize: 16, color: i < 3 ? 'white' : 'grey.500' }} />
        </Box>
      ))}
    </Box>
    <Typography variant="caption" color="text.secondary" display="block">
      3 credits available
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 1 }}>
      <Paper elevation={0} sx={{ px: 1.5, py: 0.5, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
        <Typography variant="caption" fontWeight="bold" color="primary.main">
          1 Credit = 1 Theme
        </Typography>
      </Paper>
    </Box>
  </Box>
);

const FreeVsPremiumVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        textAlign: 'center',
        bgcolor: 'success.50',
        border: 1,
        borderColor: 'success.200',
        minWidth: 90,
      }}
    >
      <CheckIcon sx={{ color: 'success.main', fontSize: 24, mb: 0.5 }} />
      <Typography variant="caption" display="block" fontWeight="bold" color="success.dark">
        Free
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: 10 }}>
        Basic colors
      </Typography>
    </Paper>
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        textAlign: 'center',
        bgcolor: 'primary.50',
        border: 1,
        borderColor: 'primary.200',
        minWidth: 90,
      }}
    >
      <CreditIcon sx={{ color: 'primary.main', fontSize: 24, mb: 0.5 }} />
      <Typography variant="caption" display="block" fontWeight="bold" color="primary.dark">
        Premium
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: 10 }}>
        Team styles & more
      </Typography>
    </Paper>
  </Box>
);

const UnlimitedUseVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1 }}>
      {[1, 2, 3].map((num) => (
        <Paper
          key={num}
          elevation={1}
          sx={{
            width: 50,
            height: 35,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" fontWeight="bold">
            Board {num}
          </Typography>
        </Paper>
      ))}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
      <CheckIcon sx={{ color: 'success.main', fontSize: 16 }} />
      <Typography variant="caption" color="success.main" fontWeight="bold">
        Same theme, unlimited boards!
      </Typography>
    </Box>
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Welcome to the Theme Store!',
    description: 'Themes let you customize the look of your boards. Choose from free themes or unlock premium designs for your games!',
    icon: <StoreIcon sx={{ fontSize: 40 }} />,
    visual: <ThemeGridVisual />,
  },
  {
    title: 'Free & Premium Themes',
    description: 'Free themes are available to everyone. Premium themes include team-inspired designs and exclusive styles, with more refined looks coming soon!',
    icon: <PaletteIcon sx={{ fontSize: 40 }} />,
    visual: <FreeVsPremiumVisual />,
  },
  {
    title: 'Credits System',
    description: 'Purchase premium themes using credits. Buy credit packs from the pricing page - the more you buy, the more you save!',
    icon: <CreditIcon sx={{ fontSize: 40 }} />,
    visual: <CreditSystemVisual />,
  },
  {
    title: 'Use Forever, Unlimited Boards',
    description: 'Once you purchase a theme, it\'s yours forever! Apply it to as many boards as you like - ownership never expires.',
    icon: <CustomizeIcon sx={{ fontSize: 40 }} />,
    visual: <UnlimitedUseVisual />,
  },
];

interface ThemeStoreEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const ThemeStoreEducationModal: React.FC<ThemeStoreEducationModalProps> = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleComplete = () => {
    markEducationModalSeen('theme_store');
    setActiveStep(0);
    onClose();
  };

  const handleSkip = () => {
    markEducationModalSeen('theme_store');
    setActiveStep(0);
    onClose();
  };

  if (!open) return null;

  const currentStep = steps[activeStep];

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden' },
      }}
    >
      <Box
        sx={{
          bgcolor: 'secondary.main',
          color: 'white',
          py: 3,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 1 }}>{currentStep.icon}</Box>
        <Typography variant="h5" fontWeight="bold">
          {currentStep.title}
        </Typography>
      </Box>

      <DialogContent sx={{ py: 4, px: 3 }}>
        <Box sx={{ mb: 3, minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {currentStep.visual}
        </Box>
        <Typography variant="body1" textAlign="center" color="text.secondary">
          {currentStep.description}
        </Typography>
      </DialogContent>

      <Box sx={{ px: 3, pb: 2 }}>
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{ bgcolor: 'transparent', justifyContent: 'center', mb: 2 }}
          backButton={<Box />}
          nextButton={<Box />}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            size="small"
            onClick={handleSkip}
            sx={{ color: 'text.secondary' }}
          >
            Skip
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<KeyboardArrowLeft />}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleNext}
            endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <KeyboardArrowRight />}
          >
            {activeStep === steps.length - 1 ? 'Start Browsing' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

// Helper to check if user has seen theme store education
export const hasSeenThemeStoreEducation = (): boolean => {
  return hasSeenEducationModal('theme_store');
};

// Helper to reset education modal (for testing)
export const resetThemeStoreEducation = (): void => {
  resetEducationModal('theme_store');
};

export default ThemeStoreEducationModal;
